// Russian. Same keys, nesting and array lengths as ka.ts (typed via Dict).
import type { Dict } from "./ka";

const ru: Dict = {
  meta: {
    title: "HOTGAME — Зажигаем!",
    description: "Грузинские игры для вечеринок на телефоне: Блефомания, Шпион, Match Party, Правда или ложь и Блокнот Джокера.",
  },
  nav: {
    games: "Игры",
    join: "Войти по коду",
    joker: "Блокнот Джокера",
    login: "Войти",
    account: "Профиль",
    brand: "Брендбук",
    admin: "Админка",
    home: "Главная",
    menu: "Меню",
  },
  common: {
    play: "Играть",
    back: "Назад",
    next: "Дальше",
    close: "Закрыть",
    cancel: "Отмена",
    save: "Сохранить",
    confirm: "Подтвердить",
    yes: "Да",
    no: "Нет",
    loading: "Загрузка…",
    retry: "Попробовать снова",
    copy: "Копировать",
    copied: "Скопировано!",
    share: "Поделиться",
    players: "Игроки",
    playersN: "Игроков: {n}",
    minutes: "{n} мин",
    you: "Ты",
    host: "Ведущий",
    bot: "Бот",
    audience: "Зритель",
    points: "Очки",
    pts: "{n} оч.",
    sound: "Звук",
    mute: "Выключить звук",
    unmute: "Включить звук",
    on: "Вкл.",
    off: "Выкл.",
    or: "или",
    seconds: "{n} с",
    round: "Раунд {n}",
    of: "{a} / {b}",
    lang: { ka: "ქართული", en: "English", ru: "Русский" },
    langShort: { ka: "ქარ", en: "ENG", ru: "РУС" },
  },
  home: {
    heroTitle: "Зажигаем!",
    // Width of heroTitle in em at weight 900 (measured), so the headline can fill the row exactly.
    heroEm: 5.8,
    heroSub: "Шесть игр для компании друзей. Телефон в руки — и погнали.",
    create: "Создать комнату",
    joinPlaceholder: "Код",
    joinGo: "Войти",
    gamesTitle: "Выбери игру",
    gamesSub: "Все игры работают на грузинском, английском и русском — и вопросы приходят на выбранном языке.",
    howTitle: "Как играть",
    how1: "Кто-то один создаёт комнату — на телефоне, ноутбуке или телевизоре.",
    how2: "Остальные заходят по QR или по коду из 4 букв. Приложение не нужно.",
    how3: "Играете, смеётесь и спорите. Очки считаем мы.",
    tryTitle: "Попробуй прямо сейчас",
    tryQ: "В 1932 году армия Австралии затеяла самую настоящую войну — и проиграла её. Победу одержали ____.",
    tryA: "эму",
    tryLies: ["кенгуру", "кролики", "коалы"],
    tryNote: "Это была «Великая война с эму»: солдаты с пулемётами так и не одолели 20 000 эму.",
    trySub: "Один ответ правдивый, остальные — враньё. Угадаешь, какой?",
    tryRight: "В точку! Так всё и было 🔥",
    tryWrong: "Тебя развели! Правильный ответ:",
    tryAgain: "Ещё разок",
    adultTitle: "Режим 18+",
    adultSub: "Когда дети уже спят. Острые вопросы про отношения, вечеринки и всё то, о чём вслух не говорят. Включается только с разрешения ведущего.",
    statQuestions: "вопросов",
    statGames: "игр",
    statLangs: "языка",
    footerTag: "Горячие игры для холодных вечеров.",
    rights: "© 2026 HOTGAME",
  },
  games: {
    bluff: {
      name: "Блефомания",
      tagline: "Наври. Раскуси. Победи.",
      short: "Придумай убедительную ложь, обмани друзей — и найди правду среди чужого вранья.",
      players: "2–12",
      time: "15–25",
      rules: [
        "На экране — странный, но реальный факт с пропущенным словом.",
        "Каждый пишет свою ложь — такую, чтобы другие приняли её за правду.",
        "При голосовании ищешь настоящий ответ: правда — +1000, каждый обманутый друг — +500.",
        "Во втором раунде очки удваиваются, в жарком финале — утраиваются.",
      ],
    },
    spy: {
      name: "Шпион",
      tagline: "Один из вас врёт.",
      short: "Секретное слово знают все, кроме шпиона. 3–5 вопросов — и кто же шпион?",
      players: "3–16",
      time: "5–10",
      rules: [
        "Все получают секретное слово. Шпион (или несколько шпионов) — только тему.",
        "Игра задаёт 3–5 вопросов. Отвечаете по очереди — не выдавай лишнего, но и не веди себя как шпион.",
        "После вопросов все голосуют за того, кого считают шпионом.",
        "У пойманного шпиона есть последний шанс: угадает слово — всё равно победит.",
      ],
    },
    match: {
      name: "Match Party",
      tagline: "Хинкали ищет пару.",
      short: "Игра на память с карточками грузинского застолья. Ищи пары, собирай комбо и берегись бомб.",
      players: "1–6",
      time: "5–10",
      rules: [
        "За ход открываешь две карточки. Совпали — пара твоя, и ты ходишь снова.",
        "Пары подряд дают комбо: +100, +150, +200…",
        "Бомба отнимает очки, и ход переходит к следующему.",
        "Играйте по очереди на одном телефоне или каждый со своего.",
      ],
    },
    truf: {
      name: "Правда или ложь",
      tagline: "10 секунд. Правда или ложь?",
      short: "Быстрая викторина: на экране утверждение, а ты решаешь — правда это или ложь. Чем быстрее, тем больше очков.",
      players: "1–30",
      time: "5",
      rules: [
        "Появляется утверждение — одни тебя поразят, другие окажутся просто известными мифами.",
        "Выбирай: правда или ложь. Быстрый верный ответ приносит больше очков.",
        "Три верных подряд — начинается серия, и бонус растёт 🔥",
      ],
    },
    guess: {
      name: "Guess — Угадай",
      tagline: "Увидел. Сказал. Передал ход.",
      short: "Быстрая голосовая дуэль на картинках: называй ответ вслух — система проверит сама. У каждого 60 секунд на весь матч.",
      players: "2–6",
      time: "3–6",
      rules: [
        "У каждого игрока 60 секунд. Верный ответ передаёт ход. Ошибка и пропуск отнимают 3 секунды.",
        "Говори ответ вслух — по-грузински или по-английски. Нажимать ничего не нужно.",
        "Скажи «skip» или «pass» — появится новая картинка, но ты потеряешь 3 секунды.",
        "Проигрывает тот, у кого первым закончится время.",
      ],
    },
    joker: {
      name: "Блокнот Джокера",
      tagline: "Очки посчитает блокнот.",
      short: "Счёт для грузинского джокера: отмечай, кто сколько заказал и сколько взял, — остальное посчитает блокнот.",
      players: "3–4",
      time: "∞",
    },
  },
  cats: {
    weird: "WTF-факты",
    animals: "Животные",
    body: "Человеческое тело",
    science: "Наука",
    space: "Космос",
    history: "История",
    laws: "Странные законы",
    money: "Бизнес и деньги",
    places: "Места",
    geo: "География",
    food: "Еда и напитки",
    popculture: "Поп-культура",
    sport: "Спорт",
    georgia: "Грузия",
    myths: "Мифы",
    tech: "Технологии",
    night: "Ночь 18+",
  },
  create: {
    title: "Новая комната",
    online: "Каждый со своего телефона",
    onlineSub: "Код комнаты и QR — друзья заходят со своих телефонов.",
    local: "На одном телефоне",
    localSub: "Передавайте телефон друг другу — интернет не нужен.",
    solo: "Соло с ботами",
    soloSub: "Потренируйся на ботах, пока не подтянулись друзья.",
    yourName: "Твоё имя",
    namePh: "Например, Нино",
    avatar: "Твой персонаж",
    roomLang: "Язык вопросов",
    go: "Создать комнату",
    nameRequired: "Сначала впиши имя",
    howToPlay: "Как играть",
  },
  join: {
    title: "Вход в комнату",
    code: "Код комнаты",
    codePh: "ABCD",
    go: "Войти",
    asAudience: "Войти зрителем",
    notFound: "Такой комнаты не нашлось. Проверь код.",
    full: "Комната заполнена — можешь зайти зрителем.",
    started: "Игра уже идёт — подключишься со следующего раунда.",
    gameIs: "Игра: {game}",
  },
  lobby: {
    title: "Лобби",
    code: "Код комнаты",
    scan: "Сканируй и заходи",
    invite: "Позови друзей",
    copyLink: "Скопировать ссылку",
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    messenger: "Messenger",
    shareText: "Заходи на HOTGAME! Игра: {game}. Код: {code}",
    waitingHost: "Ждём, когда ведущий начнёт…",
    start: "Начать",
    needPlayers: "Минимум игроков: {n}",
    addBot: "+ бот",
    removeBot: "Удалить бота",
    kick: "Выгнать",
    makeHost: "Сделать ведущим",
    settings: "Настройки",
    settingsHostOnly: "Настройки меняет ведущий",
    rename: "Изменить имя",
    becomeAudience: "Стать зрителем",
    becomePlayer: "Стать игроком",
    leave: "Выйти из комнаты",
    leaveConfirm: "Точно выходишь из комнаты?",
    lang: "Язык вопросов: {lang}",
    adult: "Вопросы 18+",
    adultConfirmTitle: "Включить режим 18+",
    adultConfirmText: "В вопросах 18+ есть шутки про секс, алкоголь и свидания. Включай, только если всем игрокам есть 18.",
    adultConfirmYes: "Нам всем есть 18 — включай",
    players: "Игроки ({n})",
    audience: "Зрители ({n})",
    offline: "Нет связи",
    tv: "Режим экрана (TV)",
  },
  room: {
    connecting: "Подключаемся к комнате…",
    reconnecting: "Связь оборвалась — переподключаемся…",
    gone: "Этой комнаты больше нет или её срок истёк.",
    kicked: "Ведущий выгнал тебя из комнаты.",
    toHome: "На главную",
    paused: "Пауза",
    pausedSub: "Ведущий поставил игру на паузу",
    pause: "Пауза",
    resume: "Продолжить",
    skip: "Пропустить",
    playAgain: "Ещё раз!",
    backToLobby: "Вернуться в лобби",
    newRound: "Следующий раунд",
    audienceNote: "Ты зритель — смотри и смейся 🍿",
    hostControls: "Пульт ведущего",
    scoreboard: "Таблица",
    winner: "Победитель!",
    winners: "Победители!",
    report: "Ошибка в вопросе?",
    reportThanks: "Спасибо! Мы проверим.",
    reportReasons: { wrong: "Факт неверный", typo: "Грамматика / опечатка", boring: "Скучно", offensive: "Оскорбительно", other: "Другое" },
  },
  bluff: {
    act0: "Раунд 1",
    act1: "Раунд 2",
    act2: "Жаркий финал",
    actSub0: "Разогреваемся",
    actSub1: "Очки удваиваются ×2",
    actSub2: "Один вопрос. Очки ×3",
    question: "Вопрос {n}/{total}",
    writeTitle: "Придумай ложь",
    writePh: "Твоя ложь…",
    send: "Отправить",
    change: "Изменить",
    help: "Придумай за меня",
    helpLeft: "Осталось: {n}",
    helpUse: "Беру",
    yourLie: "Твоя ложь",
    waitingOthers: "Ждём остальных…",
    autoLie: "Время вышло — мы написали ложь за тебя (половина очков).",
    voteTitle: "Где правда?",
    voteHint: "Свою ложь выбрать нельзя. 😂 — если какой-то ответ тебя рассмешил.",
    yours: "Твоя ложь",
    reread: "Прочитать ещё раз",
    voted: "Голос принят!",
    picked: "Твой выбор",
    likes: "Лайк",
    revealLie: "Ложь от {name}!",
    revealLies: "Ложь от {names}!",
    revealHouse: "Это мы наврали!",
    revealAuto: "Ложь от {name} (автоматическая)",
    revealTruth: "Правда!",
    fooled: "Купились:",
    found: "Угадали:",
    nobodyFound: "Правду не нашёл никто 😱",
    everyoneFound: "Правду нашли все!",
    onlyYou: "Правда только у тебя! 🔥",
    gotYou: "Тебя развели! Это всё {name}!",
    gotYouHouse: "Наша ложь тебя развела 😏",
    youFooled: "На твою ложь купились: {n}!",
    scoresTitle: "Таблица",
    finalTitle: "Итоги игры",
    awards: {
      liar: "Мастер вранья",
      detective: "Детектив",
      gullible: "Доверчивая душа",
      liked: "Фаворит публики",
      bestLie: "Лучшая ложь",
    },
    awardsSub: {
      liar: "Обманутых: {n}",
      detective: "Верных ответов: {n}",
      gullible: "Попаданий на крючок: {n}",
      liked: "Лайков: {n}",
      bestLie: "«{text}» — купились: {n}",
    },
    nextIn: "Следующий вопрос…",
    skipWrite: "К голосованию",
    skipVote: "К результатам",
    errors: {
      is_truth: "Это же правда! 😅 Придумай другую ложь.",
      too_close: "Слишком похоже на правду — попробуй что-то другое.",
      taken: "Ровно это уже кто-то написал. Придумай другое!",
      own_option: "Свою ложь выбрать нельзя 😉",
      no_help_left: "Подсказок больше нет.",
    },
  },
  spy: {
    rolesTitle: "Секретные роли",
    tapToSee: "Нажми и узнай свою роль",
    hideFromOthers: "Прикрой экран — чтобы никто не подсмотрел!",
    youAreSpy: "Ты шпион!",
    spyHint: "Слова ты не знаешь. Слушай остальных и постарайся не выдать себя.",
    spyTheme: "Тема: {pack}",
    spyNoTheme: "Ты даже тему не знаешь 😈",
    secretWord: "Секретное слово",
    civHint: "Шпион этого слова не знает. Не выдавай его, но и подозрений не вызывай!",
    spiesInGame: "Шпионов в игре: {n}",
    seen: "Всё ясно",
    seenCount: "Роль посмотрели: {a}/{b}",
    startQuestions: "Начать вопросы",
    qTitle: "Вопрос {n}/{total}",
    order: "Отвечают по очереди:",
    yourTurn: "Твоя очередь! Отвечай вслух",
    turnOf: "Отвечает: {name}",
    doneTurn: "Ответ дан ✓",
    nextQuestion: "Следующий вопрос",
    answerPh: "Твой ответ (коротко)…",
    answered: "Ответили: {a}/{b}",
    answersTitle: "Ответы",
    toVote: "К голосованию",
    voteTitle: "Кто шпион?",
    voteSub: "Выбери одного подозреваемого.",
    votedFor: "Твой голос: {name}",
    votesIn: "Проголосовали: {a}/{b}",
    closeVote: "Завершить голосование",
    groupAccuse: "Кого выбрала компания?",
    accuse: "Обвинить",
    revealTitle: "Шпион — это…",
    revealTitlePlural: "Шпионы — это…",
    caught: "Поймали! 🎯",
    escaped: "На свободе! 🏃",
    wrongAccused: "{name} — не шпион 😬",
    noOneAccused: "Голоса разделились — никого не поймали!",
    guessTitle: "Последний шанс!",
    guessSub: "Угадай секретное слово: угадаешь — и победа всё равно твоя.",
    guessWait: "Пойманный шпион угадывает слово…",
    guessed: "{name} угадывает слово! 😱",
    guessedWrong: "{name} не угадывает: «{word}»",
    civWin: "Мирные победили!",
    spyWin: "Шпионы победили!",
    wordWas: "Слово было: {word}",
    roundPts: "+{n}",
    passTo: "Передай телефон: {name}",
    passReady: "Это я, {name} — покажи",
    passHide: "Готово — прячь",
    localSetup: "Игроки",
    addPlayer: "+ игрок",
    playerPh: "Игрок {n}",
    packs: "Темы",
    allPacks: "Все темы",
    spies: "Количество шпионов",
    spiesAuto: "Авто",
    questions: "Вопросы",
    mode: "Ответы",
    modeVoice: "Вслух",
    modeText: "Письменно",
    spyHintSetting: "Шпион знает тему",
    qTime: "Время на вопрос",
    qTimeNone: "Без лимита",
  },
  match: {
    turn: "Ход: {name}",
    yourTurn: "Твой ход!",
    match: "Пара! +{n}",
    combo: "Комбо ×{n}! +{p}",
    miss: "Не совпало",
    boom: "Бум! 💥 −{n}",
    timeout: "Время вышло!",
    pairsLeft: "Осталось пар: {n}",
    pairs: "Пары",
    theme: "Карточки",
    bombs: "Бомбы",
    bombsNone: "Нет",
    turnTime: "Время на ход",
    turnTimeNone: "Без лимита",
    size: "Размер поля",
    themes: { supra: "Застолье", animals: "Животные", party: "Вечеринка", tbilisi: "Эмодзи-микс", emoji: "Рожицы" },
    hotseat: "На одном телефоне",
    addLocal: "+ игрок",
  },
  truf: {
    statement: "Утверждение {n}/{total}",
    true: "Правда",
    false: "Ложь",
    itsTrue: "Правда! ✓",
    itsFalse: "Ложь! ✗",
    correct: "Верно! +{n}",
    wrong: "Неверно",
    noAnswer: "Без ответа",
    streak: "{n} подряд 🔥",
    answered: "Ответили: {a}/{b}",
    count: "Количество утверждений",
    time: "Время на ответ",
    cats: "Темы",
    allCats: "Все темы",
    final: "Итоговая таблица",
    accuracy: "Верно {a} из {b}",
  },
  bluffSettings: {
    length: "Длина игры",
    short: "Короткая (5)",
    normal: "Обычная (8)",
    long: "Длинная (11)",
    speed: "Таймер",
    chill: "Спокойный",
    normalSpeed: "Обычный",
    fast: "Быстрый",
    cats: "Категории",
    allCats: "Все категории",
    houseLie: "Наша ложь тоже в игре",
    help: "«Придумай за меня» — сколько раз",
    jokes: "Шутки ведущего",
  },
  joker: {
    title: "Блокнот Джокера",
    sub: "Отмечай, кто сколько заказал и сколько взял, — очки, хишти и премии блокнот посчитает сам. Даже после обновления страницы ничего не пропадёт.",
    newGame: "Новая игра",
    continue: "Продолжить",
    saved: "Сохранённые игры",
    noSaved: "Пока ни одной игры. Начни новую!",
    deleteGame: "Удалить",
    deleteConfirm: "Удалить эту игру навсегда?",
    players: "Игроки",
    playerPh: "Игрок {n}",
    type: "Тип игры",
    typeStandard: "Стандартная (1–8, 9×4, 8–1, 9×4)",
    typeNines: "Девятки (9×16)",
    khishti: "Хишти",
    khishtiNone: "Не играем",
    khishtiHint: "Штраф: заказ от 1 и ни одной взятки",
    premium: "Премия в конце пульки",
    premiumOn: "Лучший результат удваивается",
    premiumErase: "У остальных стирается лучший результат",
    firstDealer: "Первый сдающий",
    start: "Открыть блокнот",
    pulka: "Пулька {n}",
    deal: "Раздача {n}",
    cards: "Карт: {n}",
    dealer: "Сдаёт: {name}",
    stepBids: "Кто сколько заказывает?",
    stepTaken: "Кто сколько взял?",
    bidOf: "{name}: заказ",
    takenOf: "{name}: взятки",
    pass: "Пас",
    forbidden: "{name} не может заказать {n} — в сумме выйдет {cards}",
    sumBids: "Сумма заказов: {a} / {b}",
    sumTaken: "Сумма взяток: {a} / {b}",
    takenMismatch: "Взяток должно быть ровно {n}",
    saveDeal: "Записать",
    editDeal: "Исправить",
    editTitle: "Исправление раздачи",
    deleteDeal: "Удалить последнюю раздачу",
    undo: "Отменить",
    total: "Итого",
    premiumBadge: "Премия",
    khishtiBadge: "Хишти",
    exact: "Ровно",
    finished: "Игра окончена!",
    rename: "Переименовать",
    reset: "Начать заново",
    resetConfirm: "Все раздачи будут удалены. Точно?",
    rules: "Правила и очки",
    rulesList: [
      "Взяток ровно столько, сколько заказано: 50 × заказ + 50 (пас = 50).",
      "Заказ на все карты и все взятки твои: 100 × количество карт.",
      "Заказ не выполнен: 10 × число взяток.",
      "Хишти: заказ от 1 и ни одной взятки.",
      "Сдающий (заказывает последним) не может назвать число, при котором сумма заказов равна количеству карт.",
      "Премия: если все раздачи пульки сыграны ровно в заказ, лучший результат пульки удваивается.",
    ],
    sync: "Синхронизировано с аккаунтом",
    localOnly: "Хранится только на этом устройстве. Войди в аккаунт, чтобы блокноты были и на других устройствах.",
    shareImage: "Сохранить картинкой",
    leader: "Лидирует: {name}",
  },
  auth: {
    loginTitle: "Вход",
    registerTitle: "Регистрация",
    withPassword: "По паролю",
    withSms: "По SMS-коду",
    withEmail: "По коду на почту",
    identifier: "Логин, почта или телефон",
    username: "Имя пользователя",
    usernameHint: "Латинские буквы, цифры, _ или . (3–20)",
    email: "Эл. почта",
    phone: "Номер телефона",
    country: "Страна",
    password: "Пароль",
    passwordHint: "Минимум 8 символов",
    displayName: "Имя в игре (необязательно)",
    terms: "Принимаю правила и подтверждаю, что мне больше 13 лет",
    login: "Войти",
    register: "Зарегистрироваться",
    noAccount: "Ещё нет аккаунта?",
    haveAccount: "Уже есть аккаунт?",
    forgot: "Не помнишь пароль?",
    sendCode: "Отправить код",
    codeSent: "Код отправлен: {to}",
    code: "6-значный код",
    verify: "Подтвердить",
    resend: "Отправить ещё раз",
    resendIn: "Отправить ещё раз через {n} сек.",
    devCode: "Тестовый режим: SMS- и почтовый провайдер ещё не подключены. Код: {code}",
    newPassword: "Новый пароль",
    resetTitle: "Восстановление пароля",
    resetDone: "Пароль изменён — вход уже выполнен.",
    logout: "Выйти",
    guestNote: "Играть можно и без регистрации. Аккаунт хранит твою статистику и блокноты Джокера.",
    profile: "Профиль",
    verifyPhone: "Подтвердить телефон",
    verifyEmail: "Подтвердить почту",
    verified: "Подтверждено",
    notVerified: "Не подтверждено",
    adultToggle: "Показывать контент 18+",
    adultConfirm: "Подтверждаю, что мне есть 18 лет",
    stats: "Статистика",
    played: "Сыграно",
    won: "Выиграно",
    best: "Рекорд",
    changePassword: "Сменить пароль",
    currentPassword: "Текущий пароль",
    saved: "Сохранено!",
    errors: {
      username_invalid: "Недопустимое имя пользователя",
      username_taken: "Это имя уже занято",
      email_invalid: "Неверный адрес почты",
      email_taken: "Эта почта уже зарегистрирована",
      phone_invalid: "Неверный номер",
      phone_taken: "Этот номер уже зарегистрирован",
      password_short: "Пароль должен быть не короче 8 символов",
      password_long: "Пароль слишком длинный",
      password_digits: "Пароль не должен состоять только из цифр",
      terms_required: "Нужно принять правила",
      bad_credentials: "Неверный логин или пароль",
      otp_invalid: "Неверный код",
      otp_expired: "Срок действия кода истёк — запроси новый",
      otp_locked: "Слишком много попыток — запроси новый код",
      delivery_failed: "Не удалось отправить код. Попробуй позже.",
      rate_limited: "Ты пробуешь слишком часто. Подожди немного.",
      auth_required: "Сначала войди в аккаунт",
      age_confirm_required: "Подтверди возраст",
      conflict: "Такой аккаунт уже существует",
      generic: "Что-то пошло не так. Попробуй ещё раз.",
    },
  },
  err: {
    not_found: "Не найдено",
    not_host: "Это может только ведущий",
    not_player: "Ты не играешь в этом раунде",
    bad_phase: "Сейчас это невозможно",
    bad_input: "Что-то введено неверно",
    too_few_players: "Не хватает игроков",
    room_full: "Комната заполнена",
    name_taken: "Это имя уже занято",
    kicked: "Тебя выгнали из комнаты",
    rate_limited: "Помедленнее 🙂",
    is_truth: "Это правда!",
    too_close: "Слишком похоже на правду",
    taken: "Такое уже написали",
    own_option: "Свой вариант выбрать нельзя",
    already: "Уже сделано",
    not_your_turn: "Сейчас не твой ход",
    no_help_left: "Подсказок больше нет",
    self_vote: "Себя обвинить нельзя 😄",
    no_content: "С такими настройками вопросов не нашлось",
    paused: "Игра на паузе",
    avatar_taken: "Этот аватар уже занят — выберите другой",
    server: "Ошибка сервера",
    name_required: "Впиши имя",
    network: "Проблемы со связью",
    csrf: "Ошибка безопасности — обнови страницу",
  },
  /** Bluffmania host lines (read by the narrator; 2–3 variants each, picked at random). */
  host: {
    welcome: [
      "Добро пожаловать в Блефоманию! Здесь правда бывает очень странной, а ложь — ваше главное оружие!",
      "Ну что, мастера вранья, поехали!",
      "Приветствую, друзья! Сегодня узнаем, кто из вас врёт красивее всех.",
    ],
    act0: ["Первый раунд! Разогреваемся.", "Начинается первый раунд. Готовьте своё враньё!"],
    act1: ["Второй раунд! Теперь очки удваиваются.", "Внимание: во втором раунде каждая ложь стоит вдвое больше!"],
    act2: ["Жаркий финал! Один вопрос, тройные очки. Сейчас решится всё!", "Финал! Тут даже лидер может слететь с вершины."],
    write: ["Придумывайте ложь!", "А теперь — время врать."],
    hurry: ["Осталось десять секунд!", "Быстрее, время уходит!"],
    timeup: ["Время вышло!", "Ручки на стол!"],
    vote: ["А теперь найдите правду!", "Один из ответов — правда. Какой?", "Выбирайте правду и не попадитесь на удочку!"],
    lie: ["Это была ложь!", "Враньё!", "Ого, а это выдумка!"],
    fooledMany: ["Сколько народу купилось!", "Браво, вот это враньё!", "Оскар за эту ложь!"],
    truth: ["А правда — это…", "А теперь — правда!", "А правильный ответ вот какой…"],
    nobody: ["Правду не нашёл никто!", "Ой-ой, купились все!"],
    everyone: ["Правду нашли все! Какие вы, оказывается, умные.", "Угадали все — в следующий раз врите получше!"],
    scores: ["Посмотрим на таблицу!", "Ну-ка, кто у нас лидирует?"],
    winner: ["И наш победитель…", "Кто же наш главный мастер вранья? Встречайте!"],
    outro: ["Спасибо за игру! До скорой встречи.", "Отличная была игра! Ещё разок?"],
    /** "Question two!" … (index = question number − 1) */
    qnum: ["Вопрос первый!", "Вопрос второй!", "Вопрос третий!", "Вопрос четвёртый!", "Вопрос пятый!", "Вопрос шестой!", "Вопрос седьмой!", "Вопрос восьмой!", "Вопрос девятый!", "Вопрос десятый!"],
    /** said instead of the plain "write" line on 2–3 questions per game (can be switched off) */
    joke: [
      "Ну-ка, напишите такую ложь, чтобы даже свекровь поверила!",
      "Если вы хоть раз говорили «уже выхожу», лёжа в кровати, — вы готовы!",
      "«Маршрутка не пришла» — классика. А теперь ваша версия!",
      "«Буду через пять минут!» — вот такая уверенность нам нужна.",
      "Пишите так же убедительно, как «с понедельника сажусь на диету».",
      "«Последняя рюмка» — главная ложь любого застолья. Ваша очередь!",
      "Хорошая ложь — как хачапури: горячая, круглая и очень убедительная!",
      "«Телефон был на беззвучном» — старый трюк. Придумайте новый!",
      "Помните «собака съела домашку»? Этот талант снова с вами!",
      "Напишите такую ложь, чтобы завтра самим в неё поверить!",
      "Представьте, что пишете прогноз погоды: уверенно и совершенно выдуманно!",
      "Врите, как в резюме: «Свободно владею английским».",
      "Врите так же красиво, как фото в приложении для знакомств!",
      "«Я на минутку» — и остался до утра. Вот так и нужно!",
      "«Я чуть-чуть, я не голодный» — и третья тарелка. Вот это мастерство!",
      "Напишите такую ложь, чтобы тамада поднял за неё тост!",
      "Врите, как в детстве: «Это не я!»",
      "Придумайте ложь, которую разобрать сложнее, чем почерк врача!",
      "Врите, как продавец: «Этот цвет вам очень идёт!»",
      "«Купила со скидкой» — чемпион семейной лжи. Ваша очередь!",
      "Запишите ложь, пока совесть не догнала!",
      "Напишите такую ложь, чтобы её внесли в Википедию!",
      "Включите тот талант, что спас вас на последнем экзамене!",
      "Врите, как друг: «Нет, я совсем не обиделся».",
      "«Завтра обязательно позвоню» — вот такую тёплую ложь!",
      "Врите, как автомеханик: «Там всего одна маленькая деталь».",
      "«Сейчас сяду заниматься» — гимн студентов. Поехали!",
      "«Обещаю, это было в последний раз» — напишите что-то такое же искреннее!",
      "Переплюньте мою ложь. Вы её знаете: «Этот вопрос простой!»",
      "Врите уверенно, как водитель, который «знает дорогу» и не включает навигатор.",
      "«Ещё одну серию — и спать», а на часах три ночи. Ваша очередь!",
      "«Нет-нет, я больше не хочу», — говорит гость и берёт четвёртый хинкали. Ну-ка!",
      "«Батарейка села» — самая древняя ложь. Придумайте новую!",
      "Время врать! Маме не скажем.",
      "Пишите смело! Кому-то за ложь дают «Оскар», а вам — очки.",
      "Напишите ложь, которую будут передавать, как бабушкин рецепт!",
      "Придумайте такую, чтобы даже мой искусственный интеллект не раскусил!",
      "Идеальной лжи нужна капля правды — как чаче немного винограда!",
      "«Я уже поел», — говорите вы маме. А теперь используйте этот талант здесь!",
      "«Я не виноват, интернет отключился» — вот так просто и убедительно!",
      "Врите, как таксист: «Это самая короткая дорога!»",
      "Врите так же невинно, как ребёнок, перемазанный шоколадом.",
      "Ложь должна быть как хороший танец: красивая, быстрая и эффектная!",
      "«Завтра встану пораньше» — говорим каждый вечер. Давайте что-то новое!",
      "«Я прочитал пользовательское соглашение» — самая большая ложь на свете. Переплюньте!",
      "«Я свою долю уже заплатил» — классика застолья. Ваша очередь!",
      "Напишите такую ложь, чтобы правда позавидовала!",
      "«Я почти готов!» — говорит каждый, кто ещё даже не одет. Вот так!",
      "Врите так же спокойно, как кот, который только что разбил вазу.",
      "Напишите ложь, которую даже моя бабушка не раскусила бы!",
    ],
    /** the first question of a replay */
    welcomeBack: ["Ого, вы снова здесь? Значит, понравилось! Спасибо, что вернулись!", "Ещё одна игра? Обожаю! Давайте врать ещё красивее.", "Спасибо, что остались! Я знал, что одной игры вам не хватит."],
    prevWinner: ["Чемпион прошлой игры, твоя корона под угрозой!", "Предупреждаем прошлого победителя: сегодня корону отберут!"],
    prevLoser: ["Кому-то в прошлой игре не повезло — давайте горячо его поддержим! Сегодня его день!", "Тому, кто был последним: не грусти, сегодня твой реванш!"],
    /** said right after the player's name (Georgian: the name in the dative, "მარიამს, …") */
    prevWinnerName: ["Чемпион прошлой игры, твоя корона под угрозой!", "Предупреждаем прошлого победителя: сегодня корону отберут!"],
    prevLoserName: ["Кому-то в прошлой игре не повезло — давайте горячо его поддержим! Сегодня его день!", "Тому, кто был последним: не грусти, сегодня твой реванш!"],
  },
  shouts: {
    fooledAll: ["Все купились! 🔥", "Вот это враньё, ну ты даёшь!", "Оскар в студию!"],
    truthFound: ["Детектив!", "Тебя не проведёшь!", "Вот это нюх!"],
    nobody: ["Правду не нашёл никто 😱", "В такое и не поверишь, да?"],
    hot: ["Жарко! 🔥", "Накаляется!", "Ой-ой-ой!"],
    spyCaught: ["Поймали!", "Тёмные очки не спасли!"],
    spyFree: ["Шпион ушёл!", "Да у нас тут Джеймс Бонд 😎"],
  },
  brand: {
    title: "Брендбук",
  },
  avatars: {
    khinkali: "Хинкали",
    churchkhela: "Чурчхела",
    pomegranate: "Гранат",
    grapes: "Саперави",
    khachapuri: "Хачапури",
    pepper: "Перчик",
    kantsi: "Канци",
    dog: "Нагази",
    cat: "Кот",
    bear: "Медведь",
    fox: "Лиса",
    owl: "Сова",
    sheep: "Овечка",
    tur: "Тур",
    tamada: "Тамада",
    spy: "Агент",
    car: "Машина",
    rooster: "Петушок",
    cow: "Бурёнка",
  },
  prefs: {
    title: "Настройки",
    sound: "Звуковые эффекты",
    narrator: "Голос диктора",
    narratorHint: "Диктор читает вопросы вслух. В одной комнате включай его только на одном устройстве — на телефоне ведущего или на телевизоре.",
    haptics: "Вибрация",
    volume: "Громкость",
    language: "Язык сайта",
  },
  local: {
    playersTitle: "Кто играет?",
    addPlayer: "+ игрок",
    addBot: "+ бот",
    playerPh: "Игрок {n}",
    min: "Минимум игроков: {n}",
    start: "Начинаем!",
    passTo: "Передай телефон",
    tapWhenReady: "Нажми, когда приготовишься",
    hideAndPass: "Спрячь и передай следующему",
    remove: "Убрать",
    resumeTitle: "Продолжить?",
    resumeText: "На этом устройстве есть незаконченная игра.",
    resume: "Продолжить",
    discard: "Заново",
  },
  demo: {
    note: "Это предварительная версия: игры работают в браузере с ботами и на одном телефоне. Онлайн-комнаты, регистрация и SMS заработают после запуска сервера.",
    serverOnly: "Эта функция заработает после запуска сервера.",
    playBots: "Играть с ботами",
  },
  notFound: {
    title: "Такой страницы мы не нашли",
    sub: "Похоже, кто-то блефанул 😏",
    home: "На главную",
  },
};

export default ru;
